import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'
import type { HorizonId, ProjectId } from 'common/types'

import type {
  CubeInfo,
  FaultInfo,
  Field,
  Gis,
  HorizonInfo,
  MapInfo,
  Marker,
  Project,
  ProjectHorizonType,
  ProjectWellType,
  Survey2dInfo,
  Well,
} from '../../types'

const initState = (id: ProjectId): Project => ({
  id,
  fields: [],
  wells: [],
  cubes: [],
  faults: [],
  horizons: [],
  surveys2ds: [],
  maps: [],
})

const initialState: Project = initState('')
export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setProject(state, action: PayloadAction<ProjectId>) {
      return initState(action.payload)
    },
    addFieldToProject(state, action: PayloadAction<Field[]>) {
      state.fields = action.payload
    },
    addWellToProject(state, action: PayloadAction<Well[]>) {
      const array: ProjectWellType[] = action.payload.map(well => {
        return { id: well.id, inclinometry: [], gis: [], markers: [] }
      })
      state.wells.push(...array)
    },
    addGisToProject(state, action: PayloadAction<Gis[]>) {
      action.payload.forEach(i => {
        const well = state.wells.filter(well => well.id === i.wellId)[0]
        if (!well.gis.includes(i.id)) {
          well.gis.push(i.id)
        }
      })
    },
    addMarkerToProject(state, action: PayloadAction<Marker[]>) {
      action.payload.forEach(i => {
        const well = state.wells.find(well => well.id === i.wellId)
        if (well && !well.markers.includes(i.id)) {
          well.markers.push(i.id)
        }
      })
    },
    /// seismic
    addCubeToProject(state, action: PayloadAction<CubeInfo[]>) {
      const uniqueNewCubes = action.payload.filter(
        newCube => !state.cubes.some(stateCube => stateCube.id === newCube.id),
      )

      state.cubes = state.cubes.concat(uniqueNewCubes).map(cubeInfo => ({
        id: cubeInfo.id,
        name: cubeInfo.name,
        domain: cubeInfo.domain,
      }))
    },
    addFaultToProject(state, action: PayloadAction<FaultInfo[]>) {
      const uniqueFaults = new Set(state.faults.concat(action.payload.map(fault => fault.id)))
      state.faults = [...uniqueFaults]
    },
    addHorizonToProject(state, action: PayloadAction<HorizonInfo[]>) {
      const stateHorizonIds = state.horizons.map(horizon => horizon.id)
      const array: ProjectHorizonType[] = action.payload
        .filter(horizon => !stateHorizonIds.includes(horizon.id))
        .map(horizon => {
          return { id: horizon.id, surveys: [], cubes: [] }
        })
      state.horizons.push(...array)
    },
    addSurveyToProject(state, action: PayloadAction<Survey2dInfo[]>) {
      const uniqueCubes = new Set(state.surveys2ds.concat(action.payload.map(survey => survey.id)))
      state.surveys2ds = [...uniqueCubes]
    },
    addCubeToProjectHorizon(
      state,
      action: PayloadAction<{ horizonId: HorizonId; cubes: CubeInfo[] }>,
    ) {
      const horizon = state.horizons.filter(horizon => horizon.id === action.payload.horizonId)[0]
      const uniqueCubes = new Set(horizon.cubes.concat(action.payload.cubes.map(cube => cube.id)))
      horizon.cubes = [...uniqueCubes]
    },
    addSurvey2dToProjectHorizon(
      state,
      action: PayloadAction<{ horizonId: HorizonId; surveys: Survey2dInfo[] }>,
    ) {
      const horizon = state.horizons.filter(horizon => horizon.id === action.payload.horizonId)[0]
      const uniqueCubes = new Set(
        horizon.surveys.concat(action.payload.surveys.map(survey => survey.id)),
      )
      horizon.surveys = [...uniqueCubes]
    },
    addMapToProject(state, action: PayloadAction<MapInfo[]>) {
      const uniqueMaps = new Set(state.maps.concat(action.payload.map(map => map.id)))
      state.maps = [...uniqueMaps]
    },
  },
})

export default projectSlice.reducer
